import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Badge from "../../components/common/Badge";
import Modal from "../../components/common/Modal";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Pagination from "../../components/common/Pagination";
import {
  useGetBusinessQuery,
  useGetBusinessStoresQuery,
  useUpdateBusinessStoreStatusMutation,
} from "../../store/api";
import { formatDateTime } from "../../utils/reporting";
import { getStoreProblemInfo } from "../../utils/problemDetails";

const BusinessStores = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { data: business } = useGetBusinessQuery(businessId);
  const { data, isLoading, isError, error } = useGetBusinessStoresQuery(
    businessId,
  );
  const [updateStoreStatus, updateStoreStatusState] =
    useUpdateBusinessStoreStatusMutation();

  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    storeId: null,
    storeName: "",
    nextStatus: "INACTIVE",
  });
  const [statusError, setStatusError] = useState("");
  const [statusLicenseMessage, setStatusLicenseMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const stores = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.stores)) return data.stores;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, [data]);

  const getStoreId = (store) => store.storeId || store.id || "";
  const getStoreName = (store) =>
    store.storeName || store.name || store.storeSlug || "Store";
  const getStoreCode = (store) => store.storeCode || store.code || "-";
  const getStoreSlug = (store) => store.storeSlug || store.slug || "-";
  const getStoreCity = (store) =>
    store.city ||
    store.address?.city ||
    store.addressCity ||
    store.location?.city ||
    "-";
  const getStoreStatus = (store) => {
    if (typeof store?.status === "string" && store.status.length > 0) {
      return store.status.toUpperCase();
    }
    if (store?.isActive === true) return "ACTIVE";
    if (store?.isActive === false) return "INACTIVE";
    return "INACTIVE";
  };
  const getStoreCreatedAtRaw = (store) =>
    store.createdAt || store.createdOn || null;

  const normalizedSearch = search.trim().toLowerCase();

  const computedStores = useMemo(
    () =>
      stores.map((store) => ({
        raw: store,
        storeId: getStoreId(store),
        storeName: getStoreName(store),
        storeCode: getStoreCode(store),
        storeSlug: getStoreSlug(store),
        city: getStoreCity(store),
        status: getStoreStatus(store),
        createdAtRaw: getStoreCreatedAtRaw(store),
      })),
    [stores],
  );

  const filteredSortedStores = useMemo(() => {
    const filtered = computedStores.filter((store) => {
      if (!normalizedSearch) return true;
      const text = [
        store.storeName,
        store.storeCode,
        store.storeSlug,
        store.city,
        store.status,
      ]
        .join(" ")
        .toLowerCase();
      return text.includes(normalizedSearch);
    });

    const sorted = [...filtered].sort((a, b) => {
      let aValue;
      let bValue;

      if (sortBy === "createdAt") {
        aValue = a.createdAtRaw ? new Date(a.createdAtRaw).getTime() : 0;
        bValue = b.createdAtRaw ? new Date(b.createdAtRaw).getTime() : 0;
      } else {
        aValue = String(a[sortBy] || "").toLowerCase();
        bValue = String(b[sortBy] || "").toLowerCase();
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [computedStores, normalizedSearch, sortBy, sortOrder]);

  const totalCount = filteredSortedStores.length;
  const startIndex = (page - 1) * pageSize;
  const pagedStores = filteredSortedStores.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, sortOrder, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, pageSize, totalCount]);

  const openStatusModal = (store) => {
    const status = getStoreStatus(store);
    setStatusError("");
    setStatusLicenseMessage("");
    setStatusModal({
      isOpen: true,
      storeId: getStoreId(store),
      storeName: getStoreName(store),
      nextStatus: status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
    });
  };

  const closeStatusModal = () => {
    setStatusModal({
      isOpen: false,
      storeId: null,
      storeName: "",
      nextStatus: "INACTIVE",
    });
  };

  const confirmStatusChange = async () => {
    setStatusError("");
    setStatusLicenseMessage("");
    try {
      await updateStoreStatus({
        businessId,
        storeId: statusModal.storeId,
        payload: { status: statusModal.nextStatus },
      }).unwrap();
      closeStatusModal();
    } catch (err) {
      const problem = getStoreProblemInfo(err, { operation: "status" });
      if (problem.showLicenseBanner) {
        setStatusLicenseMessage(problem.message);
      } else {
        setStatusError(problem.message);
      }
    }
  };

  const columns = [
    { key: "storeName", label: "Store Name" },
    { key: "storeCode", label: "Store Code" },
    { key: "storeSlug", label: "Store Slug" },
    { key: "city", label: "City" },
    { key: "status", label: "Status" },
    { key: "createdAt", label: "Created" },
    { key: "actions", label: "Actions" },
  ];

  const rows = pagedStores.map((store) => {
    const storeId = store.storeId;
    const status = store.status;
    const isActive = status === "ACTIVE";
    return {
      id: storeId,
      storeName: store.storeName,
      storeCode: store.storeCode,
      storeSlug: store.storeSlug,
      city: store.city,
      status: <Badge tone={isActive ? "success" : "neutral"}>{status}</Badge>,
      createdAt: formatDateTime(store.createdAtRaw),
      actions: (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Link
            className="font-semibold text-[#4f46e5]"
            to={`/businesses/${businessId}/stores/${storeId}`}
          >
            View
          </Link>
          <Link
            className="font-semibold text-[#4f46e5]"
            to={`/businesses/${businessId}/stores/${storeId}/edit`}
          >
            Edit
          </Link>
          <button
            type="button"
            className="font-semibold text-slate-700 hover:text-slate-900"
            onClick={() => openStatusModal(store.raw)}
          >
            {isActive ? "Deactivate" : "Activate"}
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stores"
        subtitle={`Manage stores for ${business?.businessName || "this business"}.`}
        actions={
          <div className="flex gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Back to Business
            </Button>
            <Link to={`/businesses/${businessId}/stores/new`}>
              <Button size="sm">Create Store</Button>
            </Link>
          </div>
        }
      />

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
          Stores Directory
        </div>
        <div className="border-b border-slate-100 px-6 py-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input
              label="Search"
              placeholder="Name, code, slug, city, status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 text-sm"
            />
            <Select
              label="Sort By"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="h-10 text-sm"
            >
              <option value="createdAt">Created At</option>
              <option value="storeName">Store Name</option>
              <option value="storeCode">Store Code</option>
              <option value="storeSlug">Store Slug</option>
              <option value="city">City</option>
              <option value="status">Status</option>
            </Select>
            <Select
              label="Sort Order"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
              className="h-10 text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Select>
            <Select
              label="Page Size"
              value={String(pageSize)}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="h-10 text-sm"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </Select>
          </div>
          {!isLoading && !isError && (
            <p className="mt-3 text-xs text-slate-500">
              Showing {rows.length} of {totalCount} matched stores.
            </p>
          )}
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500">Loading stores...</p>
          )}
          {isError && (
            <Alert tone="error">
              {getStoreProblemInfo(error, { operation: "load" }).message}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table
              columns={columns}
              rows={rows}
              emptyText={
                stores.length === 0
                  ? "No stores yet. Create your first store."
                  : "No stores match your current search or filters."
              }
            />
          )}
        </div>
        {!isLoading && !isError && totalCount > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        )}
      </Card>

      {statusLicenseMessage && (
        <Alert tone="warning">
          <p>{statusLicenseMessage}</p>
          <Link
            to={`/businesses/${businessId}/licenses`}
            className="mt-2 inline-block font-semibold underline"
          >
            View license information
          </Link>
        </Alert>
      )}
      {statusError && (
        <Alert tone="error">{statusError}</Alert>
      )}

      <Modal
        isOpen={statusModal.isOpen}
        onClose={closeStatusModal}
        title={`${statusModal.nextStatus === "ACTIVE" ? "Activate" : "Deactivate"} Store`}
        onConfirm={confirmStatusChange}
        confirmText={statusModal.nextStatus === "ACTIVE" ? "Activate" : "Deactivate"}
        isLoading={updateStoreStatusState.isLoading}
      >
        <p>
          Are you sure you want to{" "}
          <strong>
            {statusModal.nextStatus === "ACTIVE" ? "activate" : "deactivate"}
          </strong>{" "}
          <strong>{statusModal.storeName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default BusinessStores;
